import { KEEP_PLACE } from "@/lib/search";
import { createFileRoute } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useState } from "react";
import { EntityNotesPanel } from "@/components/app/entity-notes/entity-notes-panel";
import { orpc } from "@/lib/orpc";
import { useCan, useCanChangeRow, useFeature } from "@/lib/policy";
import { RecordTabs } from "@/components/app/patterns/record-tabs";
import { useZone } from "@/lib/facility/zone";
import { ProspectFormDialog } from "@/routes/_authenticated/_crm/crm/prospecting/-components/prospect-form-dialog";
import { SourceContactCard } from "@/routes/_authenticated/_crm/crm/prospecting/-components/source-contact-card";
import { SourceHeader } from "@/routes/_authenticated/_crm/crm/prospecting/-components/source-header";
import { SourceLinksCard } from "@/routes/_authenticated/_crm/crm/prospecting/-components/source-links-card";
import { SourceMobileBar } from "@/routes/_authenticated/_crm/crm/prospecting/-components/source-mobile-bar";
import { SourcePlaybookCard } from "@/routes/_authenticated/_crm/crm/prospecting/-components/source-playbook-card";
import { SourceTouchesCard } from "@/routes/_authenticated/_crm/crm/prospecting/-components/source-touches-card";
import { TouchDialog } from "@/routes/_authenticated/_crm/crm/prospecting/-components/touch-dialog";
import { ContactsCard } from "./-components/contacts-card";
import { SourceDocumentsCard } from "./-components/source-documents-card";
import { SourceEventsCard } from "./-components/source-events-card";
import { SourceLinkedCard } from "./-components/source-linked-card";

const TABS = [
  { value: "info", label: "Info" },
  { value: "people", label: "People" },
  { value: "activity", label: "Activity" },
  { value: "leads", label: "Leads & residents" },
  { value: "events", label: "Events" },
  { value: "documents", label: "Documents" },
  { value: "notes", label: "Notes" },
] as const;
type Tab = (typeof TABS)[number]["value"];

export const Route = createFileRoute("/_authenticated/_crm/crm/referral-sources/$referralSourceId")(
  {
    validateSearch: (search: Record<string, unknown>): { tab?: Tab } => ({
      tab: TABS.some((tab) => tab.value === search.tab) ? (search.tab as Tab) : undefined,
    }),
    loader: ({ context, params }) =>
      context.queryClient.ensureQueryData(
        orpc.crm.prospecting.get.queryOptions({
          input: { referralSourceId: params.referralSourceId },
        }),
      ),
    component: ReferralSourcePage,
  },
);

/** One source, laid out like a lead: who they are, the people there, every touch, what they sent,
 * and the notes. */
function ReferralSourcePage() {
  const { referralSourceId } = Route.useParams();
  const { tab: linkedTab } = Route.useSearch();
  const { data: source } = useSuspenseQuery(
    orpc.crm.prospecting.get.queryOptions({ input: { referralSourceId } }),
  );
  // The source and what is filed on it follow the row rule (a shared source is the organization's);
  // a lead linked to it is the lead's own building's to change.
  const canWrite = useCanChangeRow("crm:manage")(source.facilityId);
  const canLinkLeads = useCan("crm:manage", source.facilityId);
  const hasEvents = useFeature("crm-events", source.facilityId);
  const timezone = useZone(source.facilityId);
  const tabNavigate = Route.useNavigate();
  const tab: Tab = linkedTab ?? "info";
  const setTab = (next: Tab) =>
    void tabNavigate({
      search: (prev) => ({ ...prev, tab: next === "info" ? undefined : next }),
      ...KEEP_PLACE,
    });
  const [editing, setEditing] = useState(false);
  const [logging, setLogging] = useState(false);

  return (
    <div className="flex flex-col gap-5 pb-20 md:pb-0">
      <SourceHeader
        source={source}
        canWrite={canWrite}
        viewOnly={!canWrite && canLinkLeads}
        onEdit={() => setEditing(true)}
        onLogTouch={() => setLogging(true)}
        tabs={
          <RecordTabs
            label="Source sections"
            tabs={TABS.map((entry) => ({
              ...entry,
              count:
                entry.value === "people"
                  ? source.contacts.length
                  : entry.value === "leads"
                    ? source.leads.total
                    : undefined,
            }))}
            value={tab}
            onChange={setTab}
          />
        }
      />

      {tab === "info" ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="space-y-4">
            <SourceContactCard source={source} canWrite={canWrite} />
            <SourceLinksCard source={source} canWrite={canWrite} />
          </div>
          <SourcePlaybookCard source={source} />
        </div>
      ) : null}
      {tab === "people" ? (
        <ContactsCard referralSourceId={source.id} contacts={source.contacts} canWrite={canWrite} />
      ) : null}
      {tab === "activity" ? (
        <SourceTouchesCard
          source={source}
          canWrite={canWrite}
          timezone={timezone}
          onLog={() => setLogging(true)}
        />
      ) : null}
      {tab === "leads" ? (
        <SourceLinkedCard
          source={source}
          timezone={timezone}
          canWrite={canLinkLeads}
          onShowDirectoryLinks={() => setTab("info")}
        />
      ) : null}
      {tab === "events" ? (
        <SourceEventsCard source={source} timezone={timezone} canWrite={canWrite && hasEvents} />
      ) : null}
      {tab === "documents" ? (
        <SourceDocumentsCard
          referralSourceId={source.id}
          facilityId={source.facilityId}
          canWrite={canWrite}
        />
      ) : null}
      {tab === "notes" ? (
        <EntityNotesPanel
          entityType="referral_source"
          entityId={source.id}
          facilityId={source.facilityId}
          timezone={timezone}
        />
      ) : null}

      {editing ? (
        <ProspectFormDialog
          source={source}
          facilityId={source.facilityId ?? "all"}
          onClose={() => setEditing(false)}
          onSaved={() => setEditing(false)}
        />
      ) : null}
      {logging ? <TouchDialog source={source} onClose={() => setLogging(false)} /> : null}
      <SourceMobileBar source={source} canWrite={canWrite} onLogTouch={() => setLogging(true)} />
    </div>
  );
}
